import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import {
  createUploadUrl,
  startDocumentIngestion,
  uploadFileToS3,
  uploadMetadataToS3,
} from "../../lib/api/Documents";

type UploadStatus = "idle" | "uploading" | "ingesting" | "success" | "error";

export function Upload() {
  const { userInfo } = useUserInfoContext();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [accessLevel, setAccessLevel] = useState<number>(3);
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [message, setMessage] = useState<string>("");

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setStatus("idle");
      setMessage("");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !userInfo?.accessToken) {
      setMessage("Please select a file and ensure you are logged in.");
      setStatus("error");
      return;
    }

    try {
      setStatus("uploading");
      setMessage("Preparing upload...");

      const uploadResponse = await createUploadUrl(
        selectedFile.name,
        accessLevel,
        userInfo.accessToken
      );

      if (!uploadResponse?.data) {
        throw new Error("Failed to get upload URLs");
      }

      const { fileUploadUrl, metadataUploadUrl, metadataContent, documentId } =
        uploadResponse.data;

      setMessage("Uploading file to storage...");
      await uploadFileToS3(fileUploadUrl, selectedFile);

      setMessage("Uploading metadata...");
      await uploadMetadataToS3(metadataUploadUrl, metadataContent);

      setStatus("ingesting");
      setMessage("Starting knowledge base ingestion...");
      const ingestResponse = await startDocumentIngestion(
        documentId,
        userInfo.accessToken
      );

      setStatus("success");
      setMessage(
        `Document uploaded successfully. Ingestion job started (ID: ${ingestResponse?.data?.ingestionJobId}). Your document will be available in chat after indexing completes.`
      );
      setSelectedFile(null);
    } catch (error: any) {
      setStatus("error");
      setMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Upload failed. Please try again."
      );
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 720, mx: "auto" }}>
      <Typography variant="h4" gutterBottom>
        Upload Documents
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload a document to the temporary knowledge base for review. After
        ingestion completes, approve it on the Approvals page to add it to the
        main knowledge base.
      </Typography>

      <Paper sx={{ p: 3 }}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="access-level-label">Access Level (1-5)</InputLabel>
          <Select
            labelId="access-level-label"
            value={accessLevel}
            label="Access Level (1-5)"
            onChange={(e) => setAccessLevel(Number(e.target.value))}
          >
            <MenuItem value={1}>1 - Lowest restriction</MenuItem>
            <MenuItem value={2}>2</MenuItem>
            <MenuItem value={3}>3</MenuItem>
            <MenuItem value={4}>4</MenuItem>
            <MenuItem value={5}>5 - Highest restriction</MenuItem>
          </Select>
        </FormControl>

        <Button variant="outlined" component="label" sx={{ mb: 2 }}>
          Select File
          <input type="file" hidden onChange={handleFileChange} />
        </Button>

        {selectedFile && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Selected: {selectedFile.name}
          </Typography>
        )}

        <Box>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={handleUpload}
            disabled={
              !selectedFile ||
              status === "uploading" ||
              status === "ingesting"
            }
          >
            {status === "uploading" || status === "ingesting"
              ? "Processing..."
              : "Upload & Ingest"}
          </Button>
        </Box>

        {message && (
          <Alert
            severity={
              status === "error"
                ? "error"
                : status === "success"
                ? "success"
                : "info"
            }
            sx={{ mt: 3 }}
          >
            {message}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
